import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, ArrowDown, Menu, X, Phone, Mail, MapPin,
  Instagram, Facebook, PencilRuler, Blocks, Armchair, FileText, Clock,
  Sofa, BedDouble, ToyBrick, ShowerHead, CookingPot,
} from 'lucide-react';
import { setFavicon } from '../lib/favicon';
import { applySeo } from '../lib/seo';
import './dizajn.css';

const NAV = [
  { label: 'Početna', id: 'home' },
  { label: 'O nama', id: 'about' },
  { label: 'Usluge', id: 'services' },
  { label: 'Projekti', id: 'projects' },
  { label: 'Kontakti', id: 'contact' },
];

const KONTAKT = {
  phone: '+385 91 5256 937',
  phoneHref: 'tel:+385915256937',
  email: 'info@nova-stron.eu',
  emailHref: 'mailto:info@nova-stron.eu',
  uredLines: ['Nova-Stron d.o.o.', 'Ul. Ante Starčevića 32, 21300 Makarska'],
  oib: '46134576445',
  radnoVrijeme: ['Ponedjeljak - Petak: 10:00 - 18:30', 'Subota - Nedjelja: Zatvoreno'],
  mapsLink: 'https://maps.app.goo.gl/V1crftxr3vzPYsmm8',
  mapsEmbed: 'https://www.google.com/maps?q=Nova-Stron%20d.o.o.%2C%20Ul.%20Ante%20Star%C4%8Devi%C4%87a%2032%2C%2021300%20Makarska&output=embed',
  instagram: 'https://www.instagram.com/nova_stron?igsh=dXp6MWVuNXJjMzZs',
  facebook: 'https://www.facebook.com/profile.php?id=61592805285777',
  whatsapp: 'https://wa.me/385915256937',
};

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

/* ---------- shared bits ---------- */

/* ---------- header ---------- */

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-40"
      data-testid="ns-header"
    >
      {/* dark top zone fading softly into the light hero */}
      <div
        className={`absolute inset-x-0 top-0 pointer-events-none transition-all duration-500 ${scrolled ? 'h-36 lg:h-44' : 'h-52 lg:h-64'}`}
        style={{ background: 'linear-gradient(180deg, #1d1d1b 0%, rgba(29,29,27,0.95) 22%, rgba(29,29,27,0.75) 42%, rgba(29,29,27,0.45) 60%, rgba(29,29,27,0.2) 78%, rgba(29,29,27,0.07) 90%, rgba(29,29,27,0) 100%)' }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Nova Stron — početak"
          data-testid="ns-header-logo"
          className="shrink-0"
        >
          <img
            src="/grup/ns-logo-350.png?v=2"
            srcSet="/grup/ns-logo-175.png?v=2 175w, /grup/ns-logo-350.png?v=2 350w, /grup/ns-logo-525.png?v=2 525w"
            sizes="(max-width: 640px) 120px, (max-width: 1024px) 145px, 175px"
            alt="Nova-Stron"
            className="w-[120px] sm:w-[145px] lg:w-[175px] h-auto"
          />
        </button>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map(({ label, id }) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="text-[12px] tracking-[0.18em] uppercase text-[#f5f2ec]/75 hover:text-[#d8c7b0] transition-colors"
              data-testid={`ns-nav-${id}`}
            >
              {label}
            </button>
          ))}
          <button onClick={() => scrollTo('contact')} className="ns-btn-primary !py-2.5 !px-5 !text-[11px]" data-testid="ns-header-cta">
            Zatražite konzultaciju
          </button>
        </nav>
        <button onClick={() => setOpen(true)} className="lg:hidden p-2 text-[#f5f2ec]/90" aria-label="Izbornik" data-testid="ns-menu-open">
          <Menu className="w-6 h-6" strokeWidth={1.5} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#f5f2ec]/97 backdrop-blur-lg flex flex-col items-center justify-center gap-7"
            data-testid="ns-mobile-menu"
          >
            <button onClick={() => setOpen(false)} className="absolute top-5 right-5 p-2 text-[#1d1d1b]/70" aria-label="Zatvori" data-testid="ns-menu-close">
              <X className="w-6 h-6" strokeWidth={1.5} />
            </button>
            {NAV.map(({ label, id }, i) => (
              <motion.button
                key={id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + i * 0.06 }}
                onClick={() => { setOpen(false); setTimeout(() => scrollTo(id), 250); }}
                className="text-2xl font-light tracking-[0.2em] uppercase text-[#1d1d1b] hover:text-[#6d584d] transition-colors"
              >
                {label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

/* ---------- hero ---------- */

const Hero = () => {
  const [p, setP] = useState(0); // scroll progress 0..1 within hero

  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const h = window.innerHeight || 1;
        setP(Math.min(Math.max(window.scrollY / h, 0), 1));
        raf = null;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);

  const blur = 4.5 + p * 4.5;        // 4.5px at top -> 9px scrolled
  const scale = 1.05 + p * 0.028;    // gentle ~3% slow zoom
  const shift = p * 34;              // subtle vertical drift for depth

  return (
  <section id="home" data-testid="ns-hero" className="relative min-h-screen flex items-center overflow-hidden">
    {/* final hero photo — scroll-driven soft blur, slow zoom and gentle drift */}
    <div
      className="absolute inset-0 will-change-transform"
      style={{
        backgroundImage: 'url(/grup/hero-final.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: `blur(${blur.toFixed(2)}px)`,
        transform: `translate3d(0, ${shift.toFixed(1)}px, 0) scale(${scale.toFixed(4)})`,
        transition: 'filter 0.12s linear, transform 0.12s linear',
      }}
    />
    {/* very light warm taupe tint */}
    <div className="absolute inset-0" style={{ background: 'rgba(109, 88, 77, 0.08)' }} />
    {/* soft left scrim for text readability (warm, not black) */}
    <div className="absolute inset-0" style={{ background: 'linear-gradient(100deg, rgba(46,36,28,0.42) 0%, rgba(46,36,28,0.18) 42%, rgba(46,36,28,0) 68%)' }} />
    {/* seamless continuation of the dark header */}
    <div className="absolute inset-x-0 top-0 h-48 lg:h-64 pointer-events-none" style={{ background: 'linear-gradient(180deg, #1d1d1b 0%, rgba(29,29,27,0.7) 35%, rgba(29,29,27,0.3) 68%, rgba(29,29,27,0) 100%)' }} />
    {/* smooth fade into the light O NAMA section — deepens as user leaves hero */}
    <div
      className="absolute inset-x-0 bottom-0 pointer-events-none"
      style={{
        height: `${(10 + p * 8).toFixed(1)}rem`,
        background: 'linear-gradient(0deg, #f3eee5 0%, rgba(243,238,229,0.75) 40%, rgba(243,238,229,0.35) 70%, rgba(243,238,229,0) 100%)',
        transition: 'height 0.12s linear',
      }}
    />

    <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pt-36 pb-32">
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className="block mb-6 text-[11px] tracking-[0.32em] uppercase text-[#f5f2ec]/85"
      >
        Dizajn interijera · Arhitektura · Stil života
      </motion.span>

      <h1 className="ns-heading text-5xl sm:text-6xl lg:text-7xl max-w-3xl uppercase">
        {[
          { t: 'Stvaramo', accent: false },
          { t: 'prostore', accent: true },
          { t: 's karakterom', accent: false },
          { t: 'za vaš život', accent: false },
        ].map(({ t, accent }, i) => (
          <span
            key={t}
            className="block will-change-transform"
            data-testid={`ns-hero-line-${i}`}
            style={{
              transform: `translate3d(0, ${(-(p * (22 + i * 6))).toFixed(1)}px, 0)`,
              transition: 'transform 0.12s linear',
            }}
          >
            <motion.span
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className={`block ${accent ? 'text-[#d8c7b0]' : 'text-[#f5f2ec]'}`}
              style={{ textShadow: '0 2px 24px rgba(46,36,28,0.35)' }}
            >
              {t}
            </motion.span>
          </span>
        ))}
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.85 }}
        className="mt-7 text-base sm:text-lg font-light text-[#f5f2ec]/85 max-w-md leading-relaxed"
        style={{ textShadow: '0 1px 14px rgba(46,36,28,0.3)' }}
      >
        Dizajn interijera koji spaja estetiku, funkcionalnost i vaš osobni stil — od prve skice do posljednjeg detalja.
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.05 }}
        onClick={() => scrollTo('projects')}
        className="ns-btn-primary mt-9"
        data-testid="ns-hero-cta"
      >
        Naši projekti <ArrowRight className="w-4 h-4" />
      </motion.button>
    </div>

    {/* right side vertical note + scroll indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, delay: 1.3 }}
      className="hidden md:flex flex-col items-center gap-6 absolute right-10 bottom-16 z-10"
    >
      <span className="text-[10px] tracking-[0.35em] uppercase text-[#6d584d]/80" style={{ writingMode: 'vertical-rl' }}>
        Ideje koje stvaraju vrijednost
      </span>
      <span className="w-px h-16 bg-[#6d584d]/40" />
      <button onClick={() => scrollTo('about')} className="flex flex-col items-center gap-2 text-[#6d584d]/80 hover:text-[#6d584d] transition-colors" data-testid="ns-scroll-indicator">
        <span className="text-[9px] tracking-[0.35em] uppercase">Skrolajte</span>
        <ArrowDown className="w-4 h-4 animate-bounce" strokeWidth={1.5} />
      </button>
    </motion.div>
  </section>
  );
};

/* ---------- o nama ---------- */

const ONama = () => (
  <section id="about" data-testid="ns-onama" className="relative py-20 lg:py-28 overflow-hidden scroll-mt-24">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-16 items-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8 }}
        className="relative z-10"
      >
        <span className="ns-label block mb-5">O nama</span>
        <h2 className="ns-heading text-3xl sm:text-4xl lg:text-5xl">
          VIŠE OD DIZAJNA.
          <span className="block text-[#6d584d]">STVARAMO NAČIN ŽIVOTA.</span>
        </h2>
        <div className="mt-6 space-y-4 text-base font-light text-[#1d1d1b]/70 leading-relaxed max-w-md">
          <p>
            Vjerujemo da dobar interijer nije samo lijep prostor, već mjesto koje odražava karakter,
            potrebe i način života ljudi koji u njemu žive.
          </p>
          <p>
            Svaki projekt stvaramo pažljivo — od prvih ideja i prostornog planiranja do odabira
            materijala, rasvjete, namještaja i posljednjih detalja.
          </p>
          <p>
            Spajamo estetiku, funkcionalnost i osjećaj ugode kako bismo stvorili prostore koji su
            osobni, bezvremenski i stvoreni za život.
          </p>
        </div>
        <button onClick={() => scrollTo('services')} className="ns-btn-primary mt-8" data-testid="ns-onama-link">
          Saznajte više <ArrowRight className="w-4 h-4" />
        </button>
      </motion.div>

      {/* designer photo — edges dissolve softly into the warm background */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.9 }}
        className="relative -mx-6 -mt-16 lg:mx-0 lg:mt-0 lg:max-w-[460px] lg:justify-self-end pointer-events-none"
      >
        <picture>
          <source media="(max-width: 1023px)" srcSet="/grup/onama-interior-mobile.webp" />
          <img
            src="/grup/onama-interior.webp"
            alt="Interijer Nova Stron"
            loading="lazy"
            data-testid="ns-onama-photo"
            className="w-full h-auto select-none"
          />
        </picture>
      </motion.div>
    </div>
  </section>
);

/* ---------- usluge ---------- */

const USLUGE = [
  {
    n: '01',
    Icon: PencilRuler,
    t: 'Tlocrtno rješenje s dispozicijom namještaja',
    items: [
      'izmjera i analiza prostora',
      'definiranje projektnog zadatka',
      'iscrtavanje postojećeg stanja',
      'prijedlog novog tlocrtnog rješenja (2-3 varijante)',
      'plan rušenja i zidanja za odabrano tlocrtno rješenje',
      'dispozicija namještaja za odabrano tlocrtno rješenje',
      'plan rasvjete',
      'prijedlozi namještaja, rasvjete i dekoracija',
    ],
    price: '15 EUR/M²',
  },
  {
    n: '02',
    Icon: Blocks,
    t: 'Projekt interijera - 2D',
    items: [
      'svi elementi iz usluge 1 (tlocrtno rješenje s dispozicijom namještaja)',
      'karakteristični pogledi (2D pogledi na zidove)',
      'prijedlog podnih i zidnih obloga',
      'popis predloženog namještaja i materijala',
      'plan rasvjete i raspored utičnica',
      'izrada nacrta za nove pozicije vode (ako moguće i ako potrebno)',
      'specifikacije rasvjete i prijedlog dekorativnih rasvjetnih tijela',
    ],
    price: '22 EUR/M²',
  },
  {
    n: '03',
    Icon: Armchair,
    t: 'Projekt interijera - 3D',
    items: [
      'svi elementi iz projekta interijera - 2D',
      '3D vizualizacije svih prostora',
      'troškovnik s ponudama',
      'plan podnih i zidnih obloga',
      'nacrti za izradu namještaja po mjeri - uključuje unutarnji raspored namještaja, police, ladice, načine otvaranja, materijale, okove itd.',
    ],
    price: '38 EUR/M²',
  },
];

const Usluge = () => (
  <section id="services" data-testid="ns-usluge" className="relative py-24 lg:py-32 scroll-mt-16">
    <div className="ns-blob w-[40vw] h-[40vw] bg-[#d8c7b0]/50" style={{ top: '-8%', left: '-14%' }} />
    <div className="ns-blob w-[34vw] h-[34vw] bg-[#eae1d6]/60" style={{ bottom: '-10%', right: '-10%' }} />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-14 items-end mb-14 lg:mb-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <span className="ns-label block mb-5">Naše usluge</span>
          <h2 className="ns-heading text-3xl sm:text-4xl lg:text-5xl">
            OD IDEJE DO
            <span className="block text-[#6d584d]">GOTOVOG PROJEKTA.</span>
          </h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-base font-light text-[#1d1d1b]/70 leading-relaxed lg:border-l lg:border-[#6d584d]/25 lg:pl-8 max-w-md"
          data-testid="ns-usluge-intro"
        >
          Cjelovit proces dizajna interijera — od prvog razgovora i planiranja do detaljne
          dokumentacije i predaje gotovog projekta.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-6 items-stretch lg:max-w-[1100px] lg:mx-auto">
        {USLUGE.map(({ n, Icon, t, items, price }, i) => (
          <motion.article
            key={n}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, delay: i * 0.12 }}
            className="ns-usluga-card flex flex-col p-7 lg:p-5"
            data-testid={`ns-usluga-${n}`}
          >
            <div className="flex items-start justify-between mb-8 lg:mb-4">
              <span className="ns-usluga-icon" data-testid={`ns-usluga-icon-${n}`}>
                <Icon className="w-[21px] h-[21px]" strokeWidth={1.2} />
              </span>
              <span className="text-right">
                <span className="block text-4xl font-light tracking-wide text-[#6d584d]/45 leading-none">{n}</span>
                <span className="block h-px w-8 bg-[#6d584d]/30 mt-2 ml-auto" />
              </span>
            </div>

            <h3 className="text-lg lg:text-xl font-medium leading-snug text-[#1d1d1b]">{t}</h3>
            <span className="block h-px w-full bg-[#6d584d]/15 my-6 lg:my-3" />

            <p className="text-[12.5px] tracking-[0.18em] uppercase text-[#6d584d]/70 font-medium mb-3 lg:mb-2">Uključuje:</p>
            <ul className="space-y-2 lg:space-y-1.5">
              {items.map((item) => (
                <li key={item} className="flex gap-2.5 text-[15px] font-light text-[#1d1d1b]/80 leading-[1.5]">
                  <span className="mt-[9px] h-[4px] w-[4px] rounded-full border border-[#6d584d]/60 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-8 lg:pt-5">
              <span className="block h-px w-10 bg-[#6d584d]/30 mb-4 lg:mb-2.5" />
              <p className="text-[16px] text-[#1d1d1b]" data-testid={`ns-usluga-price-${n}`}>
                <span className="font-light text-[#1d1d1b]/60">Cijena:</span>{' '}
                <span className="font-medium">{price}</span>
              </p>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- naš dizajner (block before projekti) ---------- */

const NasDizajner = () => (
  <div data-testid="ns-dizajner" className="max-w-7xl mx-auto px-6 lg:px-10 pt-6 lg:pt-8 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-14 items-center">
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.75 }}
    >
      <span className="ns-label inline-flex items-center gap-3 mb-5">
        Naš dizajner <span className="h-px w-10 bg-[#6d584d]/40" />
      </span>
      <h2 className="ns-heading text-3xl sm:text-4xl">
        <span className="text-[#6d584d]">IGOR</span> PRETVARA VAŠE ŽELJE
        <span className="block">U PROSTOR U KOJEM ŽELITE ŽIVJETI.</span>
      </h2>
      <div className="mt-6 space-y-4 text-base font-light text-[#1d1d1b]/75 leading-relaxed max-w-xl">
        <p>
          Jer ne postoji univerzalan interijer ili eksterijer. Svatko ima svoj stil, svoje navike
          i vlastitu predodžbu o udobnosti. A nestandardne želje nisu problem, već prilika za
          stvaranje nečeg posebnog.
        </p>
        <p>
          Imate ideju za svoj prostor? Obratite nam se u Nova Stron — pomoći ćemo vam pretvoriti
          je u stvarnost i izraditi dizajn-projekt prilagođen vama i vašem životnom stilu.
        </p>
      </div>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.85, delay: 0.1 }}
      className="flex justify-center lg:justify-end"
    >
      <div className="relative w-full max-w-[320px] sm:max-w-[360px] lg:max-w-[400px] overflow-hidden rounded-md" data-testid="ns-dizajner-photo-block">
        <img
          src="/dizajn/igor-square.webp"
          alt="Igor — dizajner interijera Nova-Stron"
          loading="lazy"
          className="block w-full h-auto"
          data-testid="ns-dizajner-photo"
        />
        <div className="absolute inset-x-0 bottom-0 px-4 pt-10 pb-3.5 bg-gradient-to-t from-[#1d1d1b]/60 via-[#1d1d1b]/30 to-transparent" data-testid="ns-dizajner-overlay">
          <span className="block text-lg tracking-[0.16em] text-[#f5f2ec] font-medium">IGOR</span>
          <span className="block text-[10px] tracking-[0.26em] uppercase text-[#f5f2ec]/70">Dizajner interijera</span>
        </div>
      </div>
    </motion.div>
  </div>
);

/* ---------- projekti ---------- */

const PROJEKTI_DATA = [
  {
    id: 'modern-living',
    category: 'Projekt interijera',
    title: 'OBITELJSKI STAN',
    short: 'Projekt interijera za obitelj s dvoje djece, osmišljen s naglaskom na funkcionalnost i udobnost svakodnevnog života.',
    povrsina: '75,73 m²',
    materijali: 'Travertin Becagli · parket Woodtone · kamen Pietra di Torre · granit Azul Platino · keramika Kosei / Lagos',
    prostori: 'Kuhinja i dnevni boravak · spavaća soba · dječja soba · kupaonica · hodnik · balkon',
    pdf: '/dizajn/tehnicka-dokumentacija.pdf',
    pdfName: 'Tehnicka dokumentacija.pdf',
    btnText: 'Preuzmite tehnički projekt',
    prostorije: [
      { icon: 'sofa', label: 'Kuhinja i dnevni boravak' },
      { icon: 'bed', label: 'Spavaća soba' },
      { icon: 'toy', label: 'Dječja soba' },
      { icon: 'shower', label: 'Kupaonica' },
    ],
    photos: [
      { src: '/dizajn/projekt01-photo-01.jpg?v=2', alt: 'OBITELJSKI STAN — fotografija 1' },
      { src: '/dizajn/projekt01-photo-02.jpg?v=2', alt: 'OBITELJSKI STAN — fotografija 2' },
      { src: '/dizajn/projekt01-photo-03.jpg?v=2', alt: 'OBITELJSKI STAN — fotografija 3' },
      { src: '/dizajn/projekt01-photo-04.jpg?v=2', alt: 'OBITELJSKI STAN — fotografija 4' },
      { src: '/dizajn/projekt01-photo-05.jpg?v=2', alt: 'OBITELJSKI STAN — fotografija 5' },
    ],
  },
  {
    id: 'suvremena-udobnost',
    category: 'Projekt interijera',
    title: 'SUVREMENA UDOBNOST',
    short: 'Projekt interijera za bračni par, s dodatnom spavaćom sobom za goste, osmišljen kao spoj suvremenog dizajna i topline doma.',
    pdf: '/dizajn/vizualizacije-suvremena-udobnost.pdf',
    pdfName: 'Vizualizacije.pdf',
    btnText: 'Preuzmite vizualizacije',
    prostorije: [
      { icon: 'sofa', label: 'Dnevni boravak' },
      { icon: 'kitchen', label: 'Kuhinja' },
      { icon: 'bed', label: 'Spavaća soba' },
      { icon: 'shower', label: 'Kupaonica' },
    ],
    photos: [
      { src: '/dizajn/projekt02-photo-01.jpg', alt: 'SUVREMENA UDOBNOST — fotografija 1' },
      { src: '/dizajn/projekt02-photo-02.jpg', alt: 'SUVREMENA UDOBNOST — fotografija 2' },
      { src: '/dizajn/projekt02-photo-03.jpg', alt: 'SUVREMENA UDOBNOST — fotografija 3' },
      { src: '/dizajn/projekt02-photo-04.jpg', alt: 'SUVREMENA UDOBNOST — fotografija 4' },
      { src: '/dizajn/projekt02-photo-05.jpg', alt: 'SUVREMENA UDOBNOST — fotografija 5' },
    ],
  },
];

const PROSTORIJA_ICONS = { sofa: Sofa, bed: BedDouble, toy: ToyBrick, shower: ShowerHead, kitchen: CookingPot };

const ProjektCard = ({ projekt, onOpen }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [fast, setFast] = useState(false);
  const [manualKey, setManualKey] = useState(0);
  const touchRef = useRef(null);
  const count = projekt.photos.length;

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 4000);
    return () => clearInterval(t);
  }, [paused, count, manualKey]);

  const step = (e, dir) => {
    e.stopPropagation();
    setIdx((i) => (i + dir + count) % count);
    setManualKey((k) => k + 1);
  };

  const onTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const onTouchEnd = (e) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (!start) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) <= Math.abs(dy)) return;
    setFast(true);
    setIdx((i) => (i + (dx < 0 ? 1 : -1) + count) % count);
    setManualKey((k) => k + 1);
    setTimeout(() => setFast(false), 700);
  };

  return (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.65 }}
    className="group cursor-pointer"
    onClick={onOpen}
    onMouseEnter={() => setPaused(true)}
    onMouseLeave={() => setPaused(false)}
    data-testid={`ns-projekt-card-${projekt.id}`}
  >
    <div
      className="relative aspect-[3/2] overflow-hidden rounded-md bg-[#f8f4ee]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{ touchAction: 'pan-y' }}
    >
      {projekt.photos.map(({ src, alt }, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out ${
            fast ? 'duration-[600ms]' : 'duration-[1100ms]'
          } ${i === idx ? 'opacity-100' : 'opacity-0'}`}
          loading={i === 0 ? 'eager' : 'lazy'}
          data-testid={`ns-projekt-card-${projekt.id}-photo-${i}`}
        />
      ))}

      <span
        className="absolute top-3 right-3 text-[10px] tracking-[0.22em] text-[#f5f2ec] bg-[#1d1d1b]/35 backdrop-blur-sm px-2.5 py-1 rounded-full"
        data-testid={`ns-projekt-card-${projekt.id}-indicator`}
      >
        0{idx + 1} / 0{count}
      </span>

      <button
        onClick={(e) => step(e, -1)}
        aria-label="Prethodna fotografija"
        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#f5f2ec]/35 border border-[#f5f2ec]/40 backdrop-blur-sm flex items-center justify-center text-[#f5f2ec] hover:bg-[#f5f2ec]/60 hover:text-[#1d1d1b] transition-colors"
        data-testid={`ns-projekt-card-${projekt.id}-prev`}
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
      <button
        onClick={(e) => step(e, 1)}
        aria-label="Sljedeća fotografija"
        className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#f5f2ec]/35 border border-[#f5f2ec]/40 backdrop-blur-sm flex items-center justify-center text-[#f5f2ec] hover:bg-[#f5f2ec]/60 hover:text-[#1d1d1b] transition-colors"
        data-testid={`ns-projekt-card-${projekt.id}-next`}
      >
        <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
        {projekt.photos.map((_, i) => (
          <button
            key={i}
            onClick={(e) => { e.stopPropagation(); setIdx(i); }}
            aria-label={`Fotografija ${i + 1}`}
            className={`rounded-full transition-all duration-300 ${
              i === idx ? 'w-4 h-[5px] bg-[#f5f2ec]' : 'w-[5px] h-[5px] bg-[#f5f2ec]/55'
            }`}
            data-testid={`ns-projekt-card-${projekt.id}-dot-${i}`}
          />
        ))}
      </div>
    </div>

    <div className="mt-3 flex items-start justify-between gap-4">
      <div>
        <span className="text-[10px] tracking-[0.28em] uppercase text-[#6d584d]/65">{projekt.category}</span>
        <h3 className="ns-heading text-lg mt-1">{projekt.title}</h3>
        <p className="mt-1.5 text-[13px] font-light text-[#1d1d1b]/65 leading-snug">{projekt.short}</p>
        <a
          href={projekt.pdf}
          download={projekt.pdfName}
          onClick={(e) => e.stopPropagation()}
          className="ns-btn-primary !py-2 !px-4 !text-[10px] mt-3 inline-flex"
          data-testid={`ns-projekt-card-${projekt.id}-download`}
        >
          {projekt.btnText} <ArrowDown className="w-3.5 h-3.5" />
        </a>
      </div>
      <span className="shrink-0 mt-1 w-8 h-8 rounded-full border border-[#6d584d]/25 flex items-center justify-center text-[#6d584d] transition-all duration-300 group-hover:bg-[#6d584d] group-hover:text-[#f5f2ec]">
        <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
      </span>
    </div>
  </motion.article>
  );
};

const ProjektModal = ({ projekt, onClose }) => {
  const scrollRef = useRef(null);
  const [current, setCurrent] = useState(0);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setCurrent((p) => (p !== idx ? idx : p));
  }, []);

  const goTo = (i) => {
    const el = scrollRef.current;
    const clamped = Math.max(0, Math.min(projekt.photos.length - 1, i));
    if (el) el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
  };

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-6"
      data-testid="ns-projekt-modal"
    >
      <div
        className="absolute inset-0 bg-[#1d1d1b]/55 backdrop-blur-sm"
        onClick={onClose}
        data-testid="ns-projekt-modal-backdrop"
      />
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-[#f8f4ee] rounded-md shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 lg:px-10 py-4 bg-[#f8f4ee]/90 backdrop-blur border-b border-[#6d584d]/10">
          <span className="ns-label">Naši projekti</span>
          <button
            onClick={onClose}
            aria-label="Zatvori"
            className="w-9 h-9 rounded-full border border-[#6d584d]/25 flex items-center justify-center text-[#6d584d] hover:bg-[#6d584d] hover:text-[#f5f2ec] transition-colors"
            data-testid="ns-projekt-modal-close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 lg:px-10 pb-8 lg:pb-10">
          <div className="mt-6">
            <span className="text-[11px] tracking-[0.28em] uppercase text-[#6d584d]/65">{projekt.category}</span>
            <h3 className="ns-heading text-3xl sm:text-4xl mt-2" data-testid="ns-projekt-modal-title">{projekt.title}</h3>
          </div>

          <div className="mt-6">
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="ns-carousel-blend flex overflow-x-auto snap-x snap-mandatory ns-scrollbar-hide overscroll-x-contain"
              style={{ touchAction: 'pan-x', scrollbarWidth: 'none' }}
              data-testid="ns-projekt-modal-scroller"
            >
              {projekt.photos.map(({ src, alt }, i) => (
                <div key={src} className="flex-[0_0_100%] snap-center">
                  <div className="aspect-[3/2]">
                    <img
                      src={src}
                      alt={alt}
                      className="w-full h-full object-contain"
                      loading={i === 0 ? 'eager' : 'lazy'}
                      data-testid={`ns-projekt-modal-slide-${i}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => goTo(current - 1)}
                  className="w-9 h-9 rounded-full border border-[#6d584d]/25 bg-[#f5f2ec]/70 flex items-center justify-center text-[#6d584d] hover:bg-[#f5f2ec] transition-colors"
                  aria-label="Prethodna fotografija"
                  data-testid="ns-projekt-modal-prev"
                >
                  <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => goTo(current + 1)}
                  className="w-9 h-9 rounded-full border border-[#6d584d]/25 bg-[#f5f2ec]/70 flex items-center justify-center text-[#6d584d] hover:bg-[#f5f2ec] transition-colors"
                  aria-label="Sljedeća fotografija"
                  data-testid="ns-projekt-modal-next"
                >
                  <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              <span className="text-[12px] tracking-[0.3em] text-[#6d584d]/70" data-testid="ns-projekt-modal-indicator">
                0{current + 1} / 0{projekt.photos.length}
              </span>
            </div>

            <div className="mt-3 flex gap-3">
              {projekt.photos.map(({ src, alt }, i) => (
                <button
                  key={src}
                  onClick={() => goTo(i)}
                  aria-label={`Fotografija ${i + 1}: ${alt}`}
                  className={`w-20 sm:w-24 aspect-[16/10] overflow-hidden rounded-sm transition-all duration-300 ${
                    i === current ? 'opacity-100 ring-1 ring-[#6d584d]/50' : 'opacity-50 hover:opacity-80'
                  }`}
                  data-testid={`ns-projekt-modal-thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8" data-testid="ns-projekt-modal-prostorije">
            <p className="text-[11px] tracking-[0.25em] uppercase text-[#6d584d]/65 mb-5">Prostorije</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4">
              {projekt.prostorije.map(({ icon, label }, i) => {
                const Icon = PROSTORIJA_ICONS[icon];
                return (
                <div
                  key={label}
                  className={`flex items-center gap-3 ${i > 0 ? 'md:border-l md:border-[#6d584d]/15 md:pl-6' : ''}`}
                  data-testid={`ns-projekt-prostorija-${i}`}
                >
                  <Icon className="w-7 h-7 shrink-0 text-[#1d1d1b]/80" strokeWidth={1.2} />
                  <span className="text-sm font-light text-[#1d1d1b]/80 leading-snug">{label}</span>
                </div>
                );
              })}
            </div>
          </div>

          <div className="mt-8 ns-usluga-card p-6 lg:p-7 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5" data-testid="ns-projekt-modal-download-block">
            <div>
              <h4 className="ns-heading text-xl sm:text-2xl">ŽELITE VIDJETI CIJELI PROJEKT?</h4>
              <p className="mt-2 text-sm font-light text-[#1d1d1b]/70 leading-relaxed max-w-xl">
                Preuzmite kompletnu projektnu dokumentaciju i sve materijale projekta.
              </p>
              <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-[#6d584d]/60">
                PDF · Tehnička dokumentacija · Vizualizacije
              </p>
            </div>
            <a
              href={projekt.pdf}
              download={projekt.pdfName}
              className="ns-btn-primary !py-3 !px-6 !text-[12px] shrink-0 self-start lg:self-center"
              data-testid="ns-projekt-modal-download"
            >
              {projekt.btnText} <ArrowDown className="w-4 h-4" />
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Projekti = () => {
  const [openId, setOpenId] = useState(null);
  const openProjekt = PROJEKTI_DATA.find((p) => p.id === openId);

  return (
  <section id="projects" data-testid="ns-projekti" className="relative pt-32 pb-8 lg:pt-28 lg:pb-10">
    <div
      className="absolute inset-0 pointer-events-none"
      aria-hidden="true"
      style={{
        background:
          'linear-gradient(180deg, rgba(234,225,214,0) 0%, rgba(234,225,214,0.45) 34%, rgba(216,199,176,0.4) 62%, rgba(216,199,176,0.18) 86%, rgba(216,199,176,0) 100%)',
      }}
    />
    <div className="ns-blob w-[36vw] h-[36vw] bg-[#c8b8ae]/50" style={{ bottom: '-6%', right: '-12%' }} />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-14 items-end">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <span className="ns-label block mb-5">Naši projekti</span>
          <h2 className="ns-heading text-3xl sm:text-4xl lg:text-5xl">
            PROSTORI KOJI
            <span className="block text-[#6d584d]">GOVORE ZA SEBE.</span>
          </h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-base font-light text-[#1d1d1b]/70 leading-relaxed lg:border-l lg:border-[#6d584d]/25 lg:pl-8 max-w-md"
          data-testid="ns-projekti-intro"
        >
          Svaki projekt nastaje iz pažljivo osmišljenog koncepta, promišljenih detalja i želje da
          prostor bude potpuno prilagođen svom korisniku.
        </motion.p>
      </div>

      <div className="mt-12 lg:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-x-8 lg:gap-y-12" data-testid="ns-projekti-grid">
        {PROJEKTI_DATA.map((projekt) => (
          <ProjektCard key={projekt.id} projekt={projekt} onOpen={() => setOpenId(projekt.id)} />
        ))}

        <motion.article
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.65 }}
          data-testid="ns-projekt-card-vinkovci-uskoro"
        >
          <div className="relative aspect-[3/2] overflow-hidden rounded-md bg-[#f8f4ee]">
            <img
              src="/dizajn/projekt03-uskoro.jpg"
              alt="OBITELJSKA KUĆA VINKOVCI — dostupno uskoro"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              data-testid="ns-projekt-card-vinkovci-photo"
            />
          </div>
          <div className="mt-3">
            <span className="text-[10px] tracking-[0.28em] uppercase text-[#6d584d]/65">Projekt interijera</span>
            <span className="block mt-1 text-[10px] tracking-[0.28em] uppercase text-[#6d584d]" data-testid="ns-projekt-card-vinkovci-uskoro-label">Dostupno uskoro</span>
            <h3 className="ns-heading text-lg mt-1">OBITELJSKA KUĆA VINKOVCI</h3>
            <p className="mt-1.5 text-[13px] font-light text-[#1d1d1b]/65 leading-snug">
              Prostran dom osmišljen za obiteljski način života, s naglaskom na udobnost i toplinu svakodnevnog života.
            </p>
          </div>
        </motion.article>
      </div>

      {/* placeholder removed */}
    </div>

    <AnimatePresence>
      {openProjekt && <ProjektModal projekt={openProjekt} onClose={() => setOpenId(null)} />}
    </AnimatePresence>
  </section>
  );
};

/* ---------- kontakti ---------- */

const Kontakti = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', vrsta: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: `[Dizajn studio] Vrsta projekta: ${form.vrsta || '-'}\n${form.message}`,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success('Hvala! Vaš upit je uspješno poslan.');
      setForm({ name: '', email: '', phone: '', vrsta: '', message: '' });
    } catch {
      toast.error('Došlo je do pogreške. Pokušajte ponovno.');
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="contact" data-testid="ns-kontakti" className="relative pt-10 pb-24 lg:pt-12 lg:pb-28 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="ns-label block mb-5">Kontakt</span>
          <h2 className="ns-heading text-3xl sm:text-4xl lg:text-5xl">
            STUPITE U <span className="text-[#6d584d]">KONTAKT</span>
          </h2>
          <p className="mt-5 text-base font-light text-[#1d1d1b]/70 leading-relaxed">
            Imate projekt u planu? Kontaktirajte nas za besplatnu procjenu i savjetovanje.
          </p>
        </motion.div>

        <div className="mt-14 lg:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* left — contact info */}
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.75 }}
          >
            <div className="space-y-4">
              {[
                {
                  Icon: Phone,
                  label: 'Telefon',
                  testid: 'ns-kontakt-phone',
                  content: (
                    <a href={KONTAKT.phoneHref} className="text-[#1d1d1b]/80 hover:text-[#6d584d] transition-colors">
                      {KONTAKT.phone}
                    </a>
                  ),
                },
                {
                  Icon: Mail,
                  label: 'Email',
                  testid: 'ns-kontakt-email',
                  content: (
                    <a href={KONTAKT.emailHref} className="text-[#1d1d1b]/80 hover:text-[#6d584d] transition-colors">
                      {KONTAKT.email}
                    </a>
                  ),
                },
                {
                  Icon: FileText,
                  label: 'OIB',
                  testid: 'ns-kontakt-oib',
                  content: <span className="text-[#1d1d1b]/80">{KONTAKT.oib}</span>,
                },
                {
                  Icon: MapPin,
                  label: 'Ured 28A',
                  testid: 'ns-kontakt-ured',
                  content: (
                    <a
                      href={KONTAKT.mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1d1d1b]/80 hover:text-[#6d584d] transition-colors"
                    >
                      {KONTAKT.uredLines[0]}
                      <span className="block">{KONTAKT.uredLines[1]}</span>
                    </a>
                  ),
                },
                {
                  Icon: Clock,
                  label: 'Radno vrijeme',
                  testid: 'ns-kontakt-radno',
                  content: (
                    <span className="text-[#1d1d1b]/80">
                      {KONTAKT.radnoVrijeme[0]}
                      <span className="block">{KONTAKT.radnoVrijeme[1]}</span>
                    </span>
                  ),
                },
              ].map(({ Icon, label, content, testid }) => (
                <div key={label} className="ns-kontakt-item flex items-start gap-4 p-4" data-testid={testid}>
                  <span className="ns-kontakt-icon shrink-0 w-10 h-10 rounded-lg bg-[#e8dfd2]/70 border border-[#6d584d]/15 flex items-center justify-center text-[#6d584d]">
                    <Icon className="w-[17px] h-[17px]" strokeWidth={1.4} />
                  </span>
                  <div className="text-[15px] font-light leading-relaxed">
                    <p className="text-[13px] tracking-[0.14em] uppercase text-[#6d584d]/70 font-medium mb-0.5">{label}</p>
                    {content}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* right — form */}
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.75, delay: 0.12 }}
            className="ns-usluga-card p-7 lg:p-8"
          >
            <span className="ns-label block mb-4">Pošaljite upit</span>
            <h3 className="ns-heading text-2xl sm:text-3xl">KONTAKTIRAJTE NAS</h3>
            <p className="mt-3 mb-6 text-sm font-light text-[#1d1d1b]/65 leading-relaxed">
              Ispunite obrazac, a mi ćemo vas kontaktirati u najkraćem roku.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="ns-kontakt-form">
              <input className="ns-input" placeholder="Ime i prezime" required value={form.name} onChange={set('name')} data-testid="ns-form-name" />
              <input className="ns-input" type="email" placeholder="Email" required value={form.email} onChange={set('email')} data-testid="ns-form-email" />
              <input className="ns-input" placeholder="Telefon" value={form.phone} onChange={set('phone')} data-testid="ns-form-phone" />
              <select className="ns-input" value={form.vrsta} onChange={set('vrsta')} data-testid="ns-form-vrsta">
                <option value="">Vrsta projekta</option>
                <option value="Stan">Stan</option>
                <option value="Kuća">Kuća</option>
                <option value="Poslovni prostor">Poslovni prostor</option>
                <option value="Ostalo">Ostalo</option>
              </select>
              <textarea className="ns-input min-h-[120px] resize-y" placeholder="Vaša poruka" required value={form.message} onChange={set('message')} data-testid="ns-form-message" />
              <button type="submit" disabled={sending} className="ns-btn-primary w-full justify-center disabled:opacity-60" data-testid="ns-form-submit">
                {sending ? 'Slanje…' : <>Pošaljite upit <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ---------- footer ---------- */

const Footer = () => (
  <footer data-testid="ns-footer" className="relative">
    {/* smooth transition into charcoal */}
    <div className="h-24 bg-gradient-to-b from-transparent to-[#1d1d1b]/90" />
    <div className="bg-[#1d1d1b] text-[#f5f2ec]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.2fr_1fr_1.3fr_1.2fr_auto] gap-10 lg:gap-x-12 lg:gap-y-8">
        {/* brand + socials */}
        <div>
          <a href="/dizajn" aria-label="Nova-Stron — početna" data-testid="ns-footer-logo" className="inline-block lg:-mt-2">
            <img
              src="/grup/ns-logo-350.png?v=2"
              srcSet="/grup/ns-logo-175.png?v=2 175w, /grup/ns-logo-350.png?v=2 350w, /grup/ns-logo-525.png?v=2 525w"
              sizes="(max-width: 640px) 130px, 160px"
              alt="Nova-Stron"
              className="w-[130px] sm:w-[160px] h-auto"
              loading="lazy"
            />
          </a>
          <p className="mt-4 text-sm font-light text-[#f5f2ec]/55 leading-relaxed max-w-[220px]">
            Prostori koji inspiriraju. Dizajn koji ostaje.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a href={KONTAKT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="ns-footer-ig"
              className="w-9 h-9 rounded-full border border-[#f5f2ec]/20 flex items-center justify-center text-[#f5f2ec]/70 hover:bg-[#d8c7b0] hover:border-[#d8c7b0] hover:text-[#1d1d1b] transition-colors">
              <Instagram className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href={KONTAKT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="ns-footer-fb"
              className="w-9 h-9 rounded-full border border-[#f5f2ec]/20 flex items-center justify-center text-[#f5f2ec]/70 hover:bg-[#d8c7b0] hover:border-[#d8c7b0] hover:text-[#1d1d1b] transition-colors">
              <Facebook className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href={KONTAKT.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid="ns-footer-wa"
              className="w-9 h-9 rounded-full border border-[#f5f2ec]/20 flex items-center justify-center text-[#f5f2ec]/70 hover:bg-[#d8c7b0] hover:border-[#d8c7b0] hover:text-[#1d1d1b] transition-colors">
              <WhatsAppIcon className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* brze poveznice */}
        <div>
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#d8c7b0]/80 mb-5">Brze poveznice</p>
          <ul className="space-y-3">
            {NAV.map(({ label, id }) => (
              <li key={id}>
                <button onClick={() => scrollTo(id)} className="text-sm font-light text-[#f5f2ec]/60 hover:text-[#d8c7b0] transition-colors" data-testid={`ns-footer-link-${id}`}>
                  {label === 'Kontakti' ? 'Kontakt' : label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* usluge */}
        <div>
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#d8c7b0]/80 mb-5">Usluge</p>
          <ul className="space-y-3">
            {['Tlocrtno rješenje s dispozicijom namještaja', 'Projekt interijera - 2D', 'Projekt interijera - 3D'].map((u) => (
              <li key={u}>
                <button onClick={() => scrollTo('services')} className="text-left text-sm font-light text-[#f5f2ec]/60 hover:text-[#d8c7b0] transition-colors">
                  {u}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* kontakt */}
        <div>
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#d8c7b0]/80 mb-5">Kontakt</p>
          <div className="flex items-end justify-between gap-4 sm:block">
            <ul className="space-y-3.5 text-sm font-light">
            <li>
              <a href={KONTAKT.phoneHref} className="inline-flex items-center gap-3 text-[#f5f2ec]/60 hover:text-[#d8c7b0] transition-colors" data-testid="ns-footer-phone">
                <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {KONTAKT.phone}
              </a>
            </li>
            <li>
              <a href={KONTAKT.emailHref} className="inline-flex items-center gap-3 text-[#f5f2ec]/60 hover:text-[#d8c7b0] transition-colors" data-testid="ns-footer-email">
                <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {KONTAKT.email}
              </a>
            </li>
            <li>
              <a href={KONTAKT.mapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-3 text-[#f5f2ec]/60 hover:text-[#d8c7b0] transition-colors" data-testid="ns-footer-adresa">
                <MapPin className="w-3.5 h-3.5 mt-1 shrink-0" strokeWidth={1.5} />
                <span className="leading-relaxed">
                  Nova-Stron d.o.o.
                  <span className="block">Ul. Ante Starčevića 32</span>
                  <span className="block">21300 Makarska</span>
                </span>
              </a>
            </li>
          </ul>
            <a href="/" aria-label="Nova Stron Grup — početna" className="shrink-0 sm:hidden" data-testid="ns-footer-grup-logo-link-mobile">
              <img
                src="/grup/logo-grup-white.svg"
                alt="Nova Stron Grup"
                loading="lazy"
                className="w-[100px] min-[380px]:w-[135px] h-auto opacity-95"
                data-testid="ns-footer-grup-logo-mobile"
              />
            </a>
          </div>
        </div>

        {/* grup logo — own column, far right */}
        <div className="hidden sm:flex justify-center items-center lg:self-stretch lg:border-l lg:border-[#f5f2ec]/10 lg:pl-12 lg:pr-12">
          <a href="/" aria-label="Nova Stron Grup — početna" data-testid="ns-footer-grup-logo-link">
            <picture>
              <source media="(min-width: 768px)" srcSet="/grup/logo-grup-white.png" />
              <img
                src="/grup/logo-grup-white.svg"
                alt="Nova Stron Grup"
                loading="lazy"
                className="w-[100px] md:w-[170px] h-auto opacity-95 hover:opacity-100 transition-opacity duration-300"
                data-testid="ns-footer-grup-logo"
              />
            </picture>
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-8 flex flex-col sm:flex-row gap-3 items-center justify-between border-t border-[#f5f2ec]/10 pt-6">
        <span className="ns-script text-2xl text-[#d8c7b0]">Prostori s karakterom.</span>
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#f5f2ec]/40">© 2025 Nova-Stron</span>
      </div>
    </div>
  </footer>
);

/* ---------- page ---------- */

export default function DizajnSite() {
  useEffect(() => {
    setFavicon('/grup/favicon-dizajn.png');
    window.scrollTo(0, 0);
    applySeo({
      title: 'Nova-Stron dizajn',
      description:
        'Nova-Stron studio za dizajn interijera iz Makarske. Tlocrtna rješenja, 2D i 3D projekti interijera, vizualizacije i namještaj po mjeri. Kontaktirajte nas za besplatnu procjenu.',
      url: 'https://nova-stron-dizajn.eu/',
      image: 'https://nova-stron.eu/dizajn/projekt01-photo-01.jpg',
      jsonLdId: 'ns-dizajn-jsonld',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'Nova-Stron — Dizajn interijera',
        description:
          'Studio za dizajn interijera iz Makarske: tlocrtna rješenja s dispozicijom namještaja, 2D i 3D projekti interijera, vizualizacije i namještaj po mjeri.',
        url: 'https://nova-stron-dizajn.eu/',
        image: 'https://nova-stron.eu/dizajn/projekt01-photo-01.jpg',
        telephone: '+385915256937',
        email: 'info@nova-stron.eu',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Ul. Ante Starčevića 32',
          addressLocality: 'Makarska',
          postalCode: '21300',
          addressCountry: 'HR',
        },
        hasMap: KONTAKT.mapsLink,
        openingHours: 'Mo-Fr 10:00-18:30',
        sameAs: [KONTAKT.instagram, KONTAKT.facebook],
        makesOffer: [
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Tlocrtno rješenje s dispozicijom namještaja' },
            price: '15',
            priceCurrency: 'EUR',
            priceSpecification: { '@type': 'UnitPriceSpecification', price: '15', priceCurrency: 'EUR', unitText: 'm²' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Projekt interijera - 2D' },
            price: '22',
            priceCurrency: 'EUR',
            priceSpecification: { '@type': 'UnitPriceSpecification', price: '22', priceCurrency: 'EUR', unitText: 'm²' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Projekt interijera - 3D' },
            price: '38',
            priceCurrency: 'EUR',
            priceSpecification: { '@type': 'UnitPriceSpecification', price: '38', priceCurrency: 'EUR', unitText: 'm²' },
          },
        ],
      },
    });
  }, []);

  return (
    <div className="ns-dizajn min-h-screen antialiased overflow-x-clip" data-testid="dizajn-site">
      <Header />
      <Hero />
      <ONama />
      <Usluge />
      <NasDizajner />
      <Projekti />
      <Kontakti />
      <Footer />

      {/* floating WhatsApp button */}
      <motion.a
        href={KONTAKT.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Pišite nam na WhatsApp"
        data-testid="ns-whatsapp-fab"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 1.2 }}
        className="fixed bottom-5 right-5 z-[60] w-[52px] h-[52px] rounded-full bg-[#6d584d] text-[#f5f2ec] flex items-center justify-center shadow-[0_10px_28px_-10px_rgba(46,36,28,0.55)] hover:bg-[#5a463c] hover:scale-105 transition-all duration-300"
      >
        <WhatsAppIcon className="w-6 h-6" />
      </motion.a>
    </div>
  );
}
